import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  IconButton,
  Paper,
  Stack,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { toast, ToastContainer } from "react-toastify";
import { hpcTheme, HPC_COLORS } from "./hpcTheme";
import { HPC_BRAND } from "./hpcBrand";
import { createSuccessImage, downloadBlob } from "./hpcSuccessImage";

const statusLabel = (status) => {
  if (status === "under_review") return "Under review";
  if (status === "pending") return "Pending";
  if (status === "active") return "Active";
  return status;
};

const categoryLabel = (category) => {
  if (category === "adult") return "Adult";
  if (category === "student") return "Student";
  return null;
};

const roleText = (member, isFamily, groupStatus) =>
  [
    member.role === "main" ? "Main member" : "Member",
    isFamily ? categoryLabel(member.price_category) : null,
    member.status && member.status !== groupStatus ? statusLabel(member.status) : null,
  ]
    .filter(Boolean)
    .join(" · ");

const copyText = async (text, successMessage) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMessage);
  } catch {
    toast.info(text);
  }
};

const idSx = {
  fontFamily: "monospace",
  fontWeight: 800,
  color: HPC_COLORS.hpuOrange,
  whiteSpace: "nowrap",
};

const HpcSuccess = ({
  membershipId,
  fullName,
  message,
  status,
  onCopy,
  groupId,
  maxMembers,
  currentMembers,
  members = [],
  registrationDate,
  onAddMember,
  isFamily = false,
  familyTotal,
}) => {
  const [downloading, setDownloading] = useState(false);
  const isGroup = Boolean(groupId) && members.length > 0;

  const copyAllIds = () => {
    const lines = [
      `${isFamily ? "Family group" : "Group"} ID: ${groupId}`,
      ...members.map((member) => `${member.full_name || "Member"} – ${member.membership_id}`),
    ];
    copyText(lines.join("\n"), "All IDs copied");
  };

  const downloadImage = async () => {
    setDownloading(true);
    try {
      const blob = await createSuccessImage({
        groupId: isGroup ? groupId : null,
        isFamily,
        membershipId,
        fullName,
        statusText: status ? statusLabel(status) : "",
        dateText: registrationDate,
        members: members.map((member) => ({ ...member, roleText: roleText(member, isFamily, status) })),
      });
      downloadBlob(blob, `${isGroup ? groupId : membershipId}.png`);
    } catch {
      toast.error("Could not create the image. Please copy the IDs instead.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <ThemeProvider theme={hpcTheme}>
      <CssBaseline />
      {/* This page is its own scroll area, so long member lists are never cut off. */}
      <Box
        sx={{
          height: "100vh",
          "@supports (height: 100dvh)": { height: "100dvh" },
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          background: `
            radial-gradient(ellipse 70% 50% at 50% 0%, rgba(245, 166, 35, 0.2) 0%, transparent 60%),
            linear-gradient(180deg, ${HPC_COLORS.navy} 0%, ${HPC_COLORS.navyMid} 100%)
          `,
        }}
      >
        <Box
          sx={{
            minHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            py: 4,
          }}
        >
          <Paper
            sx={{
              p: { xs: 2.5, sm: 3 },
              maxWidth: isGroup ? 520 : 440,
              width: "100%",
              textAlign: "center",
              border: `1px solid rgba(245, 166, 35, 0.4)`,
              boxShadow: `0 16px 48px rgba(0,0,0,0.4), 0 0 40px ${HPC_COLORS.glow}`,
            }}
          >
            <Box
              component="img"
              src={HPC_BRAND.logoSrc}
              alt={HPC_BRAND.name}
              sx={{
                width: 120,
                mx: "auto",
                mb: 1,
                display: "block",
                filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.35))",
              }}
            />
            <Typography variant="caption" sx={{ display: "block", mb: 2, color: HPC_COLORS.silver }}>
              {HPC_BRAND.tagline}
            </Typography>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: HPC_COLORS.hpuOrange, mb: 1 }} />
            <Typography variant="h5" gutterBottom sx={{ color: HPC_COLORS.white }}>
              You&apos;re in
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: HPC_COLORS.silver }}>
              {message}
            </Typography>
            {status && (
              <Chip
                label={`Status: ${statusLabel(status)}`}
                size="small"
                sx={{
                  mb: 1,
                  bgcolor: status === "under_review" ? "rgba(245, 166, 35, 0.15)" : "rgba(255,255,255,0.08)",
                  color: HPC_COLORS.silver,
                }}
              />
            )}
            {registrationDate && (
              <Typography variant="caption" display="block" sx={{ mb: 2, color: HPC_COLORS.silver }}>
                Registered on {registrationDate}
              </Typography>
            )}

            {isGroup ? (
              <>
                <Typography variant="subtitle2" sx={{ color: HPC_COLORS.tebaBlue }}>
                  {isFamily ? "Family group ID" : "Group ID"}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} sx={{ my: 1 }}>
                  <Typography
                    variant="h4"
                    sx={{ ...idSx, letterSpacing: { xs: 1, sm: 2 }, fontSize: { xs: "1.6rem", sm: "2.125rem" } }}
                  >
                    {groupId}
                  </Typography>
                  <Tooltip title="Copy group ID">
                    <IconButton
                      size="small"
                      aria-label="Copy group ID"
                      onClick={() => copyText(groupId, "Group ID copied")}
                      sx={{ color: HPC_COLORS.silver }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Stack
                  spacing={1}
                  sx={{
                    mt: 2,
                    mb: 2,
                    textAlign: "left",
                    borderTop: "1px solid rgba(255,255,255,0.12)",
                    pt: 2,
                  }}
                >
                  {members.map((member) => (
                    <Box
                      key={member.membership_id}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: member.role === "main" ? "rgba(245, 166, 35, 0.1)" : "rgba(255,255,255,0.04)",
                        border: `1px solid ${member.role === "main" ? "rgba(245, 166, 35, 0.35)" : "rgba(255,255,255,0.08)"}`,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 700, color: HPC_COLORS.white, overflowWrap: "anywhere" }}>
                        {member.full_name || "Member"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: HPC_COLORS.silver }}>
                        {roleText(member, isFamily, status)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                        <Typography variant="body1" sx={idSx}>
                          {member.membership_id}
                        </Typography>
                        <Tooltip title="Copy membership ID">
                          <IconButton
                            size="small"
                            aria-label={`Copy membership ID for ${member.full_name || member.membership_id}`}
                            onClick={() => copyText(member.membership_id, "Membership ID copied")}
                            sx={{ color: HPC_COLORS.silver }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                <Button variant="contained" fullWidth size="large" startIcon={<ContentCopyIcon />} onClick={copyAllIds}>
                  Copy all IDs
                </Button>
              </>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ color: HPC_COLORS.tebaBlue }}>
                  Your membership ID
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ ...idSx, my: 1.5, letterSpacing: { xs: 1, sm: 2 }, fontSize: { xs: "1.6rem", sm: "2.125rem" } }}
                >
                  {membershipId}
                </Typography>
                <Button variant="contained" fullWidth size="large" onClick={onCopy}>
                  Copy membership ID
                </Button>
              </>
            )}

            <Button
              variant="outlined"
              fullWidth
              size="large"
              startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
              onClick={downloadImage}
              disabled={downloading}
              sx={{ mt: 1.5 }}
            >
              {downloading ? "Creating image..." : "Download as image"}
            </Button>

            {groupId && isFamily && (
              <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                <Typography variant="body2" sx={{ color: HPC_COLORS.silver, mb: 1.5 }}>
                  Family group {groupId} — {currentMembers} members registered
                </Typography>
                {familyTotal != null && (
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Family total: LKR {Number(familyTotal).toLocaleString("en-LK", { maximumFractionDigits: 0 })}
                  </Typography>
                )}
                {status === "active" ? (
                  <Button variant="outlined" fullWidth onClick={onAddMember}>
                    Add another family member
                  </Button>
                ) : (
                  <Typography variant="caption" sx={{ color: HPC_COLORS.silver }}>
                    More family members can be added once this membership is activated by the gym.
                  </Typography>
                )}
              </Box>
            )}
            {groupId && !isFamily && maxMembers > 1 && (
              <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                <Typography variant="body2" sx={{ color: HPC_COLORS.silver, mb: 1.5 }}>
                  Group {groupId} — {currentMembers} of {maxMembers} members registered
                </Typography>
                {currentMembers >= maxMembers ? (
                  <Chip label="Group complete" size="small" color="success" />
                ) : status === "active" ? (
                  <Button variant="outlined" fullWidth onClick={onAddMember}>
                    Add another member
                  </Button>
                ) : (
                  <Typography variant="caption" sx={{ color: HPC_COLORS.silver }}>
                    Additional members can be added once this membership is activated by the gym.
                  </Typography>
                )}
              </Box>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 2, color: HPC_COLORS.silver }}>
              {isGroup
                ? "Save these IDs for check-in and future visits."
                : "Save this ID for check-in and future visits."}
            </Typography>
          </Paper>
        </Box>
      </Box>
      <ToastContainer position="top-center" theme="dark" />
    </ThemeProvider>
  );
};

export default HpcSuccess;
